import { VendorPerformance, VendorReviews } from "@/components/VendorRatings";
import { PageTitle } from "@/components/PageHeader";
import { MessageQualityHint } from "@/components/MessageQualityHint";
import { smartEncode } from "@/lib/smsQuality";
import VendorRoles from "@/pages/dashboard/VendorRoles";
import { ManualAddVendor } from "@/pages/dashboard/fs/ManualAddVendor";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  HardHat, Loader2, ShieldCheck, ShieldAlert, Search, Building2, Users, Plus,
  ArrowRight, AlertTriangle, Copy, CheckCircle2, Clock, FileText, ExternalLink, Globe, Ban, Trash2,
} from "lucide-react";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const LINK_LABEL = {
  active: "Working", pending: "Setting up", pending_approval: "Waiting on approval", paused: "Paused",
};
const NET_LABEL = {
  approved: ["Network approved", "text-emerald-700 bg-emerald-50"],
  pending: ["Awaiting approval", "text-amber-800 bg-amber-50"],
  rejected: ["Rejected", "text-red-700 bg-red-50"],
  incomplete: ["Setting up", "text-ink/60 bg-sand-100"],
};
const VDField = ({ label, value }) => (
  <div className="min-w-0"><dt className="text-xs font-bold uppercase tracking-wider text-ink/40">{label}</dt>
    <dd className="break-words text-ink/80">{value}</dd></div>
);
const vdAddress = (v) => {
  const a = v.address_struct || {};
  const parts = [a.street, a.unit, a.city, [a.state, a.zip].filter(Boolean).join(" ").trim()].filter(Boolean);
  return parts.length ? parts.join(", ") : (v.address || "");
};

const Stat = ({ label, value, tone = "ink", testid }) => (
  <div className="rounded-xl border border-sand-200 bg-white px-4 py-3" data-testid={testid}>
    <div className={`font-heading text-2xl font-extrabold ${tone === "warn" ? "text-redline-600" : "text-ink"}`}>{value}</div>
    <div className="text-xs font-semibold text-ink/50">{label}</div>
  </div>
);

function VendorReviewPanel({ vendorId }) {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/field-services/vendor-directory/${vendorId}/reviews`);
      setD(data);
    } catch { setD({ reviews: [], rating: null, scorecard: null }); }
  }, [vendorId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const remove = async (rid) => {
    try {
      await api.delete(`/tenants/${d.reviews.find((r) => r.id === rid)?.tenant_id}/field-services/vendors/${vendorId}/reviews/${rid}`);
      toast.success("Rating removed"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove it"); }
  };
  remove.force = true;
  return (
    <div className="space-y-5 rounded-2xl border border-sand-200 bg-white p-4" data-testid="vd-reviews-panel">
      <VendorPerformance scorecard={d.scorecard} testid="vd-performance" />
      <VendorReviews reviews={d.reviews} rating={d.rating} canRate={false} onDelete={remove} testid="vd-reviews" />
    </div>
  );
}

function VendorSheet({ vendorId, onClose, onChanged }) {
  const { viewCompanyId } = useDash();
  const { enterCommunity } = useDash();
  const [d, setD] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/field-services/vendor-directory/${vendorId}`, { params: { company_id: viewCompanyId || "" } });
      setD(data);
    } catch (e) {
      toast.error(e?.response?.status === 404
        ? "That company is no longer on the platform — this alert was out of date."
        : (e?.response?.data?.detail || "Could not open that vendor"));
      onClose();
    }
  }, [vendorId, onClose, viewCompanyId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;
  const v = d.vendor;

  const addToCommunity = async () => {
    if (!target) return toast.error("Pick a community");
    setBusy(true);
    try {
      const { data } = await api.post(`/field-services/vendor-directory/${vendorId}/link`, { tenant_id: target });
      toast.success(data.held ? `${data.detail || "Added"} Their text goes out ${data.held.send_at_local} (after hours there).` : (data.detail || "Added"));
      setTarget(""); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them there"); }
    setBusy(false);
  };
  const networkApprove = async () => {
    setBusy(true);
    try { await api.post(`/field-services/vendor-directory/${vendorId}/network-approve`); toast.success("Admitted to the network — communities can now add them"); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    setBusy(false);
  };
  const networkReject = async () => {
    setBusy(true);
    try { await api.post(`/field-services/vendor-directory/${vendorId}/network-reject`, { reason: rejectReason }); toast.success("Marked rejected"); setRejecting(false); setRejectReason(""); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
    setBusy(false);
  };
  const net = NET_LABEL[v.network_status] || NET_LABEL.incomplete;

  const archiveVendor = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/field-services/vendor-directory/${vendorId}`);
      toast.success(`${data.vendor} deleted from the network${data.unassigned ? ` · ${data.unassigned} open job${data.unassigned === 1 ? "" : "s"} freed` : ""}`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete that vendor"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="vd-detail">
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="w-full sm:min-w-0 sm:flex-1">
          <div className="font-heading text-lg font-bold text-ink break-words">{v.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink/50">
            <span data-testid="vd-network-status" className={`rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide ${net[1]}`}>{net[0]}</span>
            {v.source === "self_apply" && <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-semibold uppercase tracking-wide text-indigo-700">Applied online</span>}
            {v.coi_ok && d.coi_file_url
              ? <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" />Insured thru {v.coi?.expires_on}</span>
              : v.coi?.expires_on && !d.coi_file_url
                ? <span className="inline-flex items-center gap-1 text-amber-700" data-testid="vd-coi-claimed"><ShieldAlert className="h-3.5 w-3.5" />Expiry claimed, no certificate</span>
                : <span className="inline-flex items-center gap-1 text-danger-600"><ShieldAlert className="h-3.5 w-3.5" />Insurance {v.coi?.status || "missing"}</span>}
            <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{d.properties.length} propert{d.properties.length === 1 ? "y" : "ies"}</span>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {d.can_network_approve && v.network_status === "pending" && (
            !rejecting ? (
              <>
                <button data-testid="vd-network-approve" onClick={networkApprove} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Admit to network
                </button>
                <button data-testid="vd-network-reject" onClick={() => setRejecting(true)} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50">
                  <Ban className="h-4 w-4" /> Reject
                </button>
              </>
            ) : (
              <div className="flex w-full flex-wrap items-center gap-1.5">
                <input data-testid="vd-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason (optional)" autoFocus
                  className="w-full min-w-0 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-500/30 sm:w-44" />
                <button data-testid="vd-reject-confirm" onClick={networkReject} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Confirm
                </button>
                <button data-testid="vd-reject-cancel" onClick={() => { setRejecting(false); setRejectReason(""); }}
                  className="rounded-xl border border-sand-300 px-3 py-2 text-sm font-semibold text-ink/60">Cancel</button>
              </div>
            )
          )}
          {d.portal_link && (
            <button data-testid="vd-copy-link" onClick={() => { navigator.clipboard.writeText(d.portal_link); toast.success("Vendor link copied"); }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-semibold text-ink/70 hover:border-ink/30">
              <Copy className="h-3.5 w-3.5" /> Their link
            </button>
          )}
          {d.can_network_approve && (
            archiving ? (
              <span className="inline-flex flex-wrap items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs">
                <span className="font-semibold text-red-700">Delete from the whole network?</span>
                <button data-testid="vd-archive-confirm" onClick={archiveVendor} disabled={busy}
                  className="rounded-lg bg-red-600 px-2.5 py-1 font-bold text-white hover:bg-red-700 disabled:opacity-50">Delete</button>
                <button data-testid="vd-archive-cancel" onClick={() => setArchiving(false)}
                  className="rounded-lg px-2 py-1 font-semibold text-ink/60">Cancel</button>
              </span>
            ) : (
              <button data-testid="vd-archive-btn" onClick={() => setArchiving(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )
          )}
        </div>
      </div>

      <VendorReviewPanel vendorId={vendorId} />

      {/* full application — what Redline reviews before admitting to the network */}
      <div data-testid="vd-application" className="rounded-2xl border border-sand-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
          <FileText className="h-3.5 w-3.5" /> Application
        </div>
        {(v.coi_flags || []).length > 0 && (
          <div data-testid="vd-coi-flags" className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-900">
              <AlertTriangle className="h-3.5 w-3.5" /> Check before admitting
            </div>
            {(v.coi_flags || []).map((fl, i) => <div key={i} className="text-xs font-semibold text-amber-900">• {fl.message}</div>)}
          </div>
        )}
        <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          <VDField label="Legal name" value={v.name} />
          {v.ein && <VDField label="EIN / Tax ID" value={v.ein} />}
          {v.phone && <VDField label="Phone" value={v.phone} />}
          {v.email && <VDField label="Billing email" value={v.email} />}
          {v.website && <VDField label="Website" value={<a className="break-all text-redline-600 underline" href={/^https?:/.test(v.website) ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer">{v.website}</a>} />}
          {vdAddress(v) && <VDField label="Address" value={vdAddress(v)} />}
          {(v.tax || {}).classification && <VDField label="Tax classification" value={v.tax.classification} />}
          {(v.markets || []).length > 0 && <VDField label="Serves" value={v.markets.join(", ")} />}
        </dl>
        {(v.website_profile || {}).summary && (
          <div className="mt-3 rounded-xl bg-sand-50 p-3 text-sm text-ink/70">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40"><Globe className="h-3.5 w-3.5" /> About</div>
            {v.website_profile.summary}
          </div>
        )}
        {(v.licenses || []).length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">Licenses</div>
            {v.licenses.map((l, i) => (
              <div key={i} className="text-sm text-ink/70">{[l.type, l.number && `#${l.number}`, l.state, l.expires_on && `exp ${l.expires_on}`].filter(Boolean).join(" · ")}</div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {d.coi_file_url ? (
            <a data-testid="vd-coi-view" href={d.coi_file_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink/30">
              <ShieldCheck className="h-3.5 w-3.5" /> View certificate{v.coi?.expires_on ? ` · exp ${v.coi.expires_on}` : ""} <ExternalLink className="h-3 w-3" />
            </a>
          ) : <span className="text-xs font-semibold text-redline-600">No certificate on file</span>}
          {d.w9_file_url && (
            <a data-testid="vd-w9-view" href={d.w9_file_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink/30">
              <FileText className="h-3.5 w-3.5" /> View W-9 <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div className="mt-3 rounded-xl bg-sand-100 px-4 py-3 text-xs leading-relaxed text-ink/55" data-testid="vd-agreement-info">
          Agreement: {v.agreement?.signed
            ? `signed by ${v.agreement.signed_by || "them"}${v.agreement.signed_at ? ` on ${new Date(v.agreement.signed_at).toLocaleDateString()}` : ""} · ${v.agreement.version || ""}`
            : "not signed yet"} · Trades: {(v.trades || []).map((t) => CAT_LABEL[t] || t).join(", ") || "—"}
          {v.network_reject_reason ? ` · Reject note: ${v.network_reject_reason}` : ""}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
          <Building2 className="h-3.5 w-3.5" /> Where they work
        </div>
        <div className="space-y-2">
          {d.properties.map((p) => (
            <div key={p.tenant_id} data-testid={`vd-prop-${p.tenant_id}`}
              className="flex flex-col items-start gap-2 rounded-xl border border-sand-200 bg-white px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
              <div className="w-full min-w-0 sm:flex-1">
                <div className="text-sm font-bold text-ink">{p.name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink/45">
                  <span className="rounded-full bg-sand-100 px-2 py-0.5 font-semibold">{LINK_LABEL[p.status] || p.status}</span>
                  <span>{p.primary ? `On site: ${p.primary}` : "Nobody named on site — jobs go to their office"}</span>
                  {p.backup && <span>Backup: {p.backup}</span>}
                  {p.open_jobs > 0 && <span className="font-semibold text-ink/60">{p.open_jobs} open job{p.open_jobs === 1 ? "" : "s"}</span>}
                </div>
              </div>
              <button data-testid={`vd-open-${p.tenant_id}`} onClick={() => enterCommunity(p.tenant_id, "/dashboard/field-services")}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-sand-300 px-3 py-2 text-xs font-bold text-ink/70 hover:border-ink/30 sm:w-auto sm:justify-start">
                Open Field Services <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        {d.can_write && d.candidates.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-sand-300 bg-sand-50/60 p-4">
            <span className="text-xs font-semibold text-ink/60">Put them to work at another community — their insurance, agreement and crew carry over:</span>
            <select data-testid="vd-link-select" value={target} onChange={(e) => setTarget(e.target.value)}
              className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm outline-none">
              <option value="">Pick a community…</option>
              {d.candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="vd-link-btn" onClick={addToCommunity} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white hover:bg-ink/90 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add them
            </button>
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
          <Users className="h-3.5 w-3.5" /> Their people ({d.crew.length})
        </div>
        <div className="space-y-2">
          {d.crew.map((c) => (
            <div key={c.id} data-testid={`vd-crew-${c.id}`}
              className="flex flex-col items-start gap-1.5 rounded-xl border border-sand-200 bg-white px-4 py-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-2">
              <div className="w-full min-w-0 sm:flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-ink">{c.name}</span>
                  {(c.roles || []).length > 0 && <span className="text-xs text-ink/45">{(c.roles || []).join(" · ")}</span>}
                  <span className="rounded bg-sand-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-ink/50">
                    {c.preferred_language === "es" ? "Español" : "English"}
                  </span>
                </div>
                <div className="text-xs text-ink/45">{c.phone || c.email || "no contact on file"}</div>
              </div>
              {c.verify_status === "verified"
                ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Ready</span>
                : c.verify_status === "stopped"
                  ? <span className="text-xs font-semibold text-danger-600">Opted out of texts</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Clock className="h-3.5 w-3.5" />Waiting on them</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const NET_INVITE_TEMPLATES = {
  standard: "Ask Redline invited your company to join its vendor network - communities in our portfolio send work through it. Setup takes about five minutes: your trades, your crew, your insurance certificate, your W-9, and the vendor agreement.",
  existing: "We already work together - join the Ask Redline vendor network so requests reach your crew directly and any community in our portfolio can send you work. Quick setup, no account to create.",
  short: "You're invited to join the Ask Redline vendor network. Quick setup, no account to create.",
};

const InviteVendorPanel = ({ onDone }) => {
  const [f, setF] = useState({ name: "", contact_name: "", phone: "", email: "", website: "", trades: [], message: NET_INVITE_TEMPLATES.standard });
  const [preset, setPreset] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const applyPreset = (key) => { setPreset(key); setF((p) => ({ ...p, message: NET_INVITE_TEMPLATES[key] })); };

  const send = async () => {
    if (!f.name.trim()) return toast.error("Company name is required");
    if (!f.phone.trim() && !f.email.trim()) return toast.error("Add a mobile number or an email");
    setBusy(true);
    try {
      const { data } = await api.post("/field-services/vendor-directory/invite", f);
      setRes(data);
      toast.success(data.existing ? "We already know this company" : `Invite sent to ${data.sent_to}`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the invite"); }
    setBusy(false);
  };

  const T = (k, label, testid, type = "text") => (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">{label}</span>
      <input data-testid={testid} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="w-full rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
    </label>
  );

  if (res) return (
    <div className="space-y-3" data-testid="vd-invite-done">
      <p className="text-sm text-ink/70">
        {res.existing
          ? `${res.name} is already on the platform (${(NET_LABEL[res.network_status] || NET_LABEL.incomplete)[0].toLowerCase()}). Here's their setup link — no second record was created.`
          : "Invite sent. They finish setup from this link, then they'll show up here for network approval."}
      </p>
      <div className="flex items-center gap-2">
        <input readOnly value={res.link} data-testid="vd-invite-link"
          className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-sand-50 px-3 py-2.5 text-xs text-ink/60" />
        <button data-testid="vd-invite-copy" onClick={() => { navigator.clipboard.writeText(res.link); toast.success("Link copied"); }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white">
          <Copy className="h-4 w-4" /> Copy
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="vd-invite-form">
      <p className="text-sm text-ink/55">
        They join the <strong>network</strong> first — no community attached. Once they finish setup and
        you admit them, any property can switch them on.
      </p>
      {T("name", "Legal business name", "vd-invite-name")}
      {T("contact_name", "Office contact", "vd-invite-contact")}
      <div className="grid gap-3 sm:grid-cols-2">
        {T("phone", "Mobile number", "vd-invite-phone")}
        {T("email", "Email", "vd-invite-email", "email")}
      </div>
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Trades</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(CAT_LABEL).map(([k, label]) => {
            const on = f.trades.includes(k);
            return (
              <button key={k} data-testid={`vd-invite-trade-${k}`} onClick={() => setF({
                ...f, trades: on ? f.trades.filter((x) => x !== k) : [...f.trades, k],
              })} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-redline-500 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Your message to them</div>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {[["standard", "Standard invite"], ["existing", "Existing vendor"], ["short", "Short & sweet"]].map(([k, label]) => (
            <button key={k} type="button" data-testid={`vd-invite-preset-${k}`} onClick={() => applyPreset(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${preset === k ? "bg-ink text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
              {label}
            </button>
          ))}
        </div>
        <textarea data-testid="vd-invite-message" value={f.message} rows={4} maxLength={800}
          onChange={(e) => { setPreset(""); setF({ ...f, message: e.target.value }); }}
          placeholder="Write the invite in your own words…"
          className="w-full rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-redline-500/30" />
        <p className="mt-1.5 text-xs text-ink/45">Their secure setup link is added to the end automatically — the count below includes it.</p>
        <MessageQualityHint testId="vd-invite-sms-quality"
          text={`${f.message}\n\n${window.location.origin}/vendor/${"x".repeat(32)}`}
          onClean={() => { setPreset(""); setF((p) => ({ ...p, message: smartEncode(p.message) })); }} />
      </div>
      <button data-testid="vd-invite-send" onClick={send} disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Send the invite
      </button>
    </div>
  );
};

export default function VendorDirectory() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [trade, setTrade] = useState("");
  const [view, setView] = useState("all");
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");
  const [inviting, setInviting] = useState(false);
  const [manualAdding, setManualAdding] = useState(false);
  const { role, viewCompanyId } = useDash();
  const [sp, setSp] = useSearchParams();
  const canInvite = role === "super_admin" || role === "platform_admin";
  const applyUrl = `${window.location.origin}/vendor-apply`;
  // Vendor crew roles live here now (a Settings tab), not a separate nav item. Super-admin only.
  const tab = (sp.get("tab") === "settings" && role === "super_admin") ? "settings" : "directory";
  const goTab = (t) => setSp((prev) => { const n = new URLSearchParams(prev); if (t === "settings") n.set("tab", "settings"); else n.delete("tab"); return n; }, { replace: true });
  const TabsBar = role === "super_admin" ? (
    <div className="flex gap-1.5 border-b border-sand-200" data-testid="vd-tabs">
      {[["directory", "Directory"], ["settings", "Settings"]].map(([k, label]) => (
        <button key={k} data-testid={`vd-tab-${k}`} onClick={() => goTab(k)}
          className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-bold transition-colors ${tab === k ? "border-redline-500 text-ink" : "border-transparent text-ink/45 hover:text-ink/70"}`}>
          {label}
        </button>
      ))}
    </div>
  ) : null;
  // The bell deep-links straight to the company that's waiting on approval.
  useEffect(() => { const v = sp.get("vendor"); if (v) setOpen(v); }, [sp]);
  const closeSheet = () => {
    setOpen(null);
    if (sp.get("vendor")) { sp.delete("vendor"); setSp(sp, { replace: true }); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/field-services/vendor-directory", {
        params: { q, trade, view: view === "all" ? "" : view, company_id: viewCompanyId || "" },
      });
      setD(data); setError("");
    } catch (e) { setError(e?.response?.data?.detail || "Could not load the vendor directory"); }
  }, [q, trade, view, viewCompanyId]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  if (tab === "settings") return (
    <div className="space-y-6" data-testid="vendor-directory-page">
      <div>
        <PageTitle title="Vendor directory" about="Manage the network and the crew-role labels vendors pick during onboarding." />
      </div>
      {TabsBar}
      <VendorRoles />
    </div>
  );
  if (error) return (
    <div className="mx-auto max-w-lg py-16 text-center" data-testid="vd-error">
      <HardHat className="mx-auto h-8 w-8 text-ink/25" />
      <p className="mt-4 text-sm text-ink/55">{error}</p>
    </div>
  );
  if (!d) return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;

  return (
    <div className="space-y-6" data-testid="vendor-directory-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageTitle title="Vendor directory" about={<>Every company working across {d.scope === "platform" ? "the platform" : "your portfolio"} — one identity,
            one certificate, one crew list, however many buildings they cover.</>} />
        </div>
        {canInvite && (
          <div className="flex items-center gap-2">
            <button data-testid="vd-add-manual-btn" onClick={() => setManualAdding(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm font-bold text-ink/75 transition-colors hover:border-ink/30">
              <Plus className="h-4 w-4 text-redline-500" /> Add manually
            </button>
            <button data-testid="vd-invite-btn" onClick={() => setInviting(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-ink/85">
              <Plus className="h-4 w-4" /> Invite a vendor
            </button>
          </div>
        )}
      </div>

      {TabsBar}

      <div className="rounded-2xl border border-sand-200 bg-white p-5" data-testid="vd-signup-link-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
              <Globe className="h-3.5 w-3.5" /> Vendor sign-up link
            </div>
            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-ink/60">
              Hand this to any company you want in the network — a bid you liked, a referral from a board.
              They apply themselves, finish their own insurance and W-9, and land here for your approval.
            </p>
          </div>
          <a href="/vendor-network" target="_blank" rel="noreferrer" data-testid="vd-signup-preview"
            className="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold text-ink/50 hover:text-ink">
            See what they see <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={applyUrl} data-testid="vd-signup-link"
            className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-sand-50 px-3 py-2.5 text-xs text-ink/60" />
          <button data-testid="vd-signup-copy" onClick={() => { navigator.clipboard.writeText(applyUrl); toast.success("Sign-up link copied"); }}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white">
            <Copy className="h-4 w-4" /> Copy
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Companies" value={d.totals.vendors} testid="vd-stat-vendors" />
        <Stat label="Awaiting approval" value={d.totals.pending} tone={d.totals.pending ? "warn" : "ink"} testid="vd-stat-pending" />
        <Stat label="Insured & current" value={d.totals.insured} testid="vd-stat-insured" />
        <Stat label="Need attention" value={d.totals.attention} tone={d.totals.attention ? "warn" : "ink"} testid="vd-stat-attention" />
        <Stat label="Open jobs" value={d.totals.open_jobs} testid="vd-stat-jobs" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/30" />
          <input data-testid="vd-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a company…"
            className="w-full rounded-xl border border-sand-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        </div>
        <select data-testid="vd-trade" value={trade} onChange={(e) => setTrade(e.target.value)}
          className="rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
          <option value="">All trades</option>
          {(d.categories || []).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
        </select>
        {[["all", "All"], ["pending", d.totals.pending ? `Pending approval · ${d.totals.pending}` : "Pending approval"], ["attention", "Needs attention"]].map(([k, label]) => (
          <button key={k} data-testid={`vd-view-${k}`} onClick={() => setView(k)}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              view === k ? "bg-ink text-white"
                : k === "pending" && d.totals.pending ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                  : "bg-sand-100 text-ink/55 hover:bg-sand-200"}`}>
            {label}
          </button>
        ))}
      </div>

      {d.vendors.length === 0 && (
        <div className="rounded-xl border border-dashed border-sand-300 py-14 text-center text-sm text-ink/45" data-testid="vd-empty">
          No vendors match that. Companies appear here as soon as a community invites them.
        </div>
      )}

      <div className="space-y-2">
        {d.vendors.map((v) => (
          <button key={v.id} data-testid={`vd-row-${v.id}`} onClick={() => setOpen(v.id)}
            className="flex w-full flex-col items-start gap-2 rounded-xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink/25 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
            <div className="w-full min-w-0 sm:flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-ink">{v.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${(NET_LABEL[v.network_status] || NET_LABEL.incomplete)[1]}`}>{(NET_LABEL[v.network_status] || NET_LABEL.incomplete)[0]}</span>
                {v.source === "self_apply" && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">Applied</span>}
                {v.properties.length > 1 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-bold uppercase text-ink/60">
                    <Building2 className="h-3 w-3" />{v.properties.length} properties
                  </span>
                )}
              </div>
              <div className="mt-1 truncate text-xs text-ink/45">
                {(v.properties || []).map((p) => p.name).join(" · ") || "no community yet"}
              </div>
              <div className="mt-0.5 text-xs text-ink/40">
                {(v.trades || []).map((t) => CAT_LABEL[t] || t).join(" · ") || "no trades set"}
              </div>
            </div>
            <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold sm:w-auto">
              {v.coi_ok
                ? <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" />Insured</span>
                : <span className="inline-flex items-center gap-1 text-danger-600"><ShieldAlert className="h-3.5 w-3.5" />Not insured</span>}
              <span className="text-ink/45">{v.crew.ready} crew ready</span>
              {v.crew.pending > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />{v.crew.pending} pending</span>
              )}
              {v.open_jobs > 0 && <span className="text-ink/60">{v.open_jobs} open</span>}
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && closeSheet()}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>Vendor</DialogTitle></DialogHeader>
          {open && <VendorSheet vendorId={open} onClose={closeSheet} onChanged={load} />}
        </DialogContent>
      </Dialog>

      <Dialog open={inviting} onOpenChange={(o) => !o && setInviting(false)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Invite a vendor to the network</DialogTitle></DialogHeader>
          <InviteVendorPanel onDone={load} />
        </DialogContent>
      </Dialog>

      <Dialog open={manualAdding} onOpenChange={(o) => !o && setManualAdding(false)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Add a vendor manually</DialogTitle></DialogHeader>
          <ManualAddVendor mode="network" onDone={load} onClose={() => setManualAdding(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
